import React from 'react';
import './SessionSummary.css';

export const SessionSummary = ({
  sessionData,
  studyTime,
  onClose,
  onContinue
}) => {
  if (!sessionData) return null;

  const {
    cardsReviewed,
    correctAnswers,
    incorrectAnswers,
    ratings,
    studyMode,
    deckName
  } = sessionData;

  const accuracy = cardsReviewed > 0
    ? Math.round((correctAnswers / cardsReviewed) * 100)
    : 0;

  const getPerformanceMessage = () => {
    if (accuracy >= 90) return { text: "Outstanding! 🌟", color: "#22c55e" };
    if (accuracy >= 75) return { text: "Great work! 👏", color: "#3b82f6" };
    if (accuracy >= 60) return { text: "Good effort! 👍", color: "#f59e0b" };
    return { text: "Keep practicing! 💪", color: "#ef4444" };
  };

  const getSuggestions = () => {
    const suggestions = [];

    if (accuracy < 70) {
      suggestions.push("Review difficult cards more frequently");
      suggestions.push("Try typing practice mode for better retention");
    }

    if (cardsReviewed < 10) {
      suggestions.push("Study more cards per session for better progress");
    }

    if (ratings && ratings.again > ratings.easy) {
      suggestions.push("Take more time to understand each card");
    }

    if (suggestions.length === 0) {
      suggestions.push("Keep up the consistent study habits!");
      suggestions.push("Try challenging yourself with new decks");
    }

    return suggestions;
  };

  const performance = getPerformanceMessage();
  const suggestions = getSuggestions();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="session-summary" onClick={(e) => e.stopPropagation()}>
        <div className="session-summary-header">
          <h2>Session Complete!</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="session-summary-content">
          {/* Performance Badge */}
          <div className="performance-badge" style={{ borderColor: performance.color }}>
            <div className="performance-message" style={{ color: performance.color }}>
              {performance.text}
            </div>
            <div className="accuracy-circle">
              <svg className="accuracy-ring" viewBox="0 0 100 100">
                <circle
                  className="accuracy-ring-bg"
                  cx="50"
                  cy="50"
                  r="40"
                />
                <circle
                  className="accuracy-ring-progress"
                  cx="50"
                  cy="50"
                  r="40"
                  style={{
                    stroke: performance.color,
                    strokeDasharray: `${accuracy * 2.51} 251.2`
                  }}
                />
              </svg>
              <div className="accuracy-value">{accuracy}%</div>
              <div className="accuracy-label">Accuracy</div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="session-stats-grid">
            <div className="session-stat">
              <div className="session-stat-icon">📚</div>
              <div className="session-stat-value">{cardsReviewed}</div>
              <div className="session-stat-label">Cards Reviewed</div>
            </div>
            <div className="session-stat">
              <div className="session-stat-icon">✓</div>
              <div className="session-stat-value">{correctAnswers}</div>
              <div className="session-stat-label">Correct</div>
            </div>
            <div className="session-stat">
              <div className="session-stat-icon">✗</div>
              <div className="session-stat-value">{incorrectAnswers}</div>
              <div className="session-stat-label">Incorrect</div>
            </div>
            <div className="session-stat">
              <div className="session-stat-icon">⏱</div>
              <div className="session-stat-value">{studyTime}</div>
              <div className="session-stat-label">Time Spent</div>
            </div>
          </div>

          {/* Study Details */}
          <div className="session-details">
            <div className="session-detail-item">
              <span className="session-detail-label">Study Mode:</span>
              <span className="session-detail-value">
                {studyMode === 'flashcard' && '🃏 Flashcard'}
                {studyMode === 'quiz' && '❓ Multiple Choice'}
                {studyMode === 'typing' && '⌨️ Typing Practice'}
              </span>
            </div>
            <div className="session-detail-item">
              <span className="session-detail-label">Deck:</span>
              <span className="session-detail-value">{deckName}</span>
            </div>
          </div>

          {/* Suggestions */}
          <div className="session-suggestions">
            <h3>💡 Tips for Improvement</h3>
            <ul className="suggestions-list">
              {suggestions.map((suggestion, index) => (
                <li key={index}>{suggestion}</li>
              ))}
            </ul>
          </div>

          {/* Actions */}
          <div className="session-actions">
            <button className="session-btn session-btn-secondary" onClick={onClose}>
              View Statistics
            </button>
            <button className="session-btn session-btn-primary" onClick={onContinue}>
              Continue Studying
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
